import React, { useState } from 'react';
import { Card } from './ui/card';
import { Globe, ChevronDown, Package, Tag, ListChecks, TrendingUp, TrendingDown, Minus, FileDown } from 'lucide-react';
import SAR from './SAR';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend } from 'recharts';

const COLORS = ['#1E3A8A', '#0F766E', '#D97706', '#DC2626', '#7C3AED', '#0891B2', '#65A30D', '#DB2777'];

const fmt = (n) => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 2 });

function TxTable({ txs }) {
  return (
    <div className="overflow-x-auto max-h-[360px] overflow-y-auto bg-white">
      <table className="w-full text-xs" data-testid="product-country-tx-table">
        <thead className="bg-accent/30 sticky top-0">
          <tr className="text-foreground/55">
            <th className="py-2 px-3 text-right">التاريخ</th>
            <th className="py-2 px-3 text-right">الموظف</th>
            <th className="py-2 px-3 text-right">الصنف</th>
            <th className="py-2 px-3 text-right">الفرع</th>
            <th className="py-2 px-3 text-right">الكمية</th>
            <th className="py-2 px-3 text-right">العميل</th>
            <th className="py-2 px-3 text-right">كود خصم</th>
            <th className="py-2 px-3 text-left">المبلغ</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {txs.map((s) => (
            <tr key={s.id} className={s.used_discount_code ? 'bg-amber-50/40' : ''}>
              <td className="py-2 px-3 font-numbers whitespace-nowrap">
                {(s.date || '').slice(0, 10)}
                {s.created_at && (
                  <span className="block text-[10px] text-foreground/55">
                    {new Date(s.created_at).toLocaleTimeString('en-US', { timeZone: 'Asia/Riyadh', hour: '2-digit', minute: '2-digit', hour12: true })}
                  </span>
                )}
              </td>
              <td className="py-2 px-3">{s.employee_name || '—'}</td>
              <td className="py-2 px-3">{s.price_option_label || '—'}</td>
              <td className="py-2 px-3">{s.branch_name || '—'}</td>
              <td className="py-2 px-3 font-numbers text-center">{s.quantity}</td>
              <td className="py-2 px-3">{s.customer_name || '—'}</td>
              <td className="py-2 px-3">
                {s.used_discount_code ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-100 text-amber-700 font-bold text-[10px]">
                    <Tag className="h-3 w-3" />{s.discount_pct ? `${s.discount_pct}%` : 'كود خصم'}
                  </span>
                ) : <span className="text-foreground/30">—</span>}
              </td>
              <td className="py-2 px-3 text-left font-numbers font-bold text-emerald-700">{fmt(s.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Growth({ o }) {
  const g = Number(o.growth_pct || 0);
  if (o.trend === 'new') return <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">جديد</span>;
  if (o.trend === 'lost') return <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-700">مفقود</span>;
  const up = g > 0; const flat = g === 0;
  const Icon = flat ? Minus : up ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-0.5 font-numbers font-bold text-xs ${flat ? 'text-foreground/50' : up ? 'text-emerald-600' : 'text-red-600'}`} dir="ltr">
      <Icon className="h-3 w-3" />{up ? '+' : ''}{g.toFixed(1)}%
    </span>
  );
}

const GRID = 'grid grid-cols-[minmax(0,1fr)_36px_44px_72px_56px] gap-1.5 sm:gap-2';

function OptionsTable({ options, total, compare, onCompareChange }) {
  if (!options || options.length === 0) return null;
  const pie = options.filter((o) => o.total > 0).map((o) => ({ name: o.label, value: o.total }));
  const modeBtn = (key, label) => (
    <button type="button" onClick={() => onCompareChange?.(key)}
      className={`px-2 py-0.5 rounded-md text-[10px] font-bold border transition-colors ${compare === key ? 'bg-primary text-white border-primary' : 'bg-white border-border text-foreground/60 hover:bg-accent/40'}`}
      data-testid={`option-compare-${key}`}>{label}</button>
  );
  return (
    <div className="border-b border-border" data-testid="product-options-breakdown">
      <div className="px-4 py-2 text-xs font-bold bg-primary/5 text-primary flex flex-wrap items-center gap-2">
        <ListChecks className="h-4 w-4" /> المبيعات حسب الصنف
        <span className="flex items-center gap-1 mr-auto">
          <span className="text-[10px] font-normal text-foreground/50">المقارنة:</span>
          {modeBtn('same', 'نفس عدد الأيام')}
          {modeBtn('full', 'الفترة السابقة كاملة')}
        </span>
      </div>
      {pie.length > 0 && (
        <div className="h-56 px-2" data-testid="product-options-pie">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={pie} dataKey="value" nameKey="name" innerRadius="45%" outerRadius="80%" paddingAngle={2}>
                {pie.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v, n) => [`${fmt(v)} (${total ? ((v / total) * 100).toFixed(1) : 0}%)`, n]} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}
      <div className={`${GRID} px-4 py-2 text-xs text-foreground/55 bg-accent/20`}>
        <span>الصنف</span><span className="text-center">الكمية</span><span className="text-center">العمليات</span><span className="text-left">الإجمالي</span><span className="text-center">النمو</span>
      </div>
      {options.map((o) => (
        <div key={o.label} className={`${GRID} px-4 py-2 text-sm items-center border-t border-border`} data-testid={`product-option-row-${o.label}`}>
          <span className="font-bold min-w-0">
            <span className="block leading-tight text-[13px] break-words">{o.label}</span>
            <span className="block h-1 mt-1 rounded bg-primary/15 overflow-hidden">
              <span className="block h-full bg-primary" style={{ width: `${total ? (o.total / total) * 100 : 0}%` }} />
            </span>
          </span>
          <span className="text-center font-numbers font-bold">{o.quantity}</span>
          <span className="text-center font-numbers">{o.transactions}</span>
          <span className="text-left font-numbers font-bold text-emerald-700">
            {fmt(o.total)}
            <span className="block text-[10px] font-normal text-foreground/45">سابق {fmt(o.previous_total)}</span>
          </span>
          <span className="text-center" data-testid={`product-option-growth-${o.label}`}><Growth o={o} /></span>
        </div>
      ))}
    </div>
  );
}

export const ProductDetailTab = ({ data, onExportPdf, exportingPdf, compare = 'same', onCompareChange }) => {
  const [open, setOpen] = useState({});
  if (!data) return <Card className="p-12 text-center text-foreground/50">جاري التحميل...</Card>;
  const countries = data.countries || [];
  return (
    <Card className="p-0 overflow-hidden" data-testid="product-detail-view">
      <div className="p-4 border-b border-border bg-accent/40 flex flex-wrap items-center gap-3">
        <h3 className="font-bold flex items-center gap-2 flex-1 min-w-[200px]">
          <Package className="h-4 w-4 text-brand_blue" />
          <span data-testid="product-detail-name">{data.product_name}</span>
          <span className="text-xs text-foreground/55 font-normal">— مبيعات المنتج حسب الدولة</span>
        </h3>
        {onExportPdf && (
          <button type="button" onClick={onExportPdf} disabled={exportingPdf}
            className="inline-flex items-center gap-1.5 px-3 h-8 rounded-md bg-red-600 text-white text-xs font-bold hover:bg-red-700 disabled:opacity-60 transition-colors"
            data-testid="product-detail-export-pdf">
            <FileDown className="h-3.5 w-3.5" /> {exportingPdf ? 'جاري التصدير...' : 'تصدير PDF'}
          </button>
        )}
        <span className="text-xs font-bold px-2 py-1 rounded-md bg-white border border-border" data-testid="product-detail-total-qty">
          الكمية: <span className="font-numbers">{data.quantity}</span>
        </span>
        <span className="text-xs font-bold px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="product-detail-total-amount">
          الإجمالي: <span className="font-numbers">{fmt(data.total)}</span> <SAR />
        </span>
      </div>
      <OptionsTable options={data.options} total={data.total} compare={compare} onCompareChange={onCompareChange} />
      {countries.length === 0 ? (
        <p className="p-8 text-center text-foreground/50" data-testid="product-detail-empty">لا توجد مبيعات لهذا المنتج في الفترة المحددة.</p>
      ) : (
        <div className="divide-y divide-border" data-testid="product-detail-countries">
          <div className="grid grid-cols-[1fr_80px_120px_24px] gap-2 px-4 py-2 text-xs text-foreground/55 bg-accent/20">
            <span>الدولة</span><span className="text-center">الكمية</span><span className="text-left">الإجمالي</span><span />
          </div>
          {countries.map((c) => (
            <div key={c.country}>
              <button
                type="button"
                onClick={() => setOpen((o) => ({ ...o, [c.country]: !o[c.country] }))}
                className="w-full grid grid-cols-[1fr_80px_120px_24px] gap-2 items-center px-4 py-3 text-sm text-right hover:bg-accent/30 transition-colors"
                data-testid={`product-country-row-${c.country}`}
              >
                <span className="font-bold flex items-center gap-2"><Globe className="h-4 w-4 text-amber-600" />{c.country}</span>
                <span className="text-center font-numbers font-bold" data-testid={`product-country-qty-${c.country}`}>{c.quantity}</span>
                <span className="text-left font-numbers font-bold text-emerald-700" data-testid={`product-country-total-${c.country}`}>{fmt(c.total)}</span>
                <ChevronDown className={`h-4 w-4 text-foreground/40 transition-transform ${open[c.country] ? 'rotate-180' : ''}`} />
              </button>
              {open[c.country] && <TxTable txs={c.transactions} />}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};
